import type { ReactNode } from "react";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";

type PanelTone = "default" | "danger";

interface SettingsPanelProps {
  title: string;
  description?: string;
  icon: IconName;
  tone?: PanelTone;
  children: ReactNode;
}

const TONE = {
  default: {
    line: "border-border/40",
    icon: "text-brand",
    title: "text-foreground",
  },
  danger: {
    line: "border-danger/[0.18]",
    icon: "text-danger",
    title: "text-danger",
  },
} as const;

export function SettingsPanel({
  title,
  description,
  icon,
  tone = "default",
  children,
}: SettingsPanelProps) {
  const s = TONE[tone];

  return (
    <section className={cn("border-t pt-6", s.line)}>
      <div className="mb-6 grid gap-3 sm:grid-cols-[1.25rem_minmax(0,1fr)]">
        <Icon name={icon} className={cn("mt-1 hidden h-4 w-4 sm:block", s.icon)} />
        <div className="space-y-1.5">
          <h2 className={cn("text-xl font-semibold tracking-tight", s.title)}>{title}</h2>
          {description ? <p className="max-w-2xl text-sm leading-6 text-muted-foreground/72">{description}</p> : null}
        </div>
      </div>
      <div className="sm:pl-8">{children}</div>
    </section>
  );
}
